import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PlayArrow as TestIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Public as PublicIcon,
  Lock as LockIcon
} from '@mui/icons-material';

interface Application {
  id: number;
  name: string;
  type: string;
  status: 'active' | 'inactive';
  config_data: any;
  created_at: string;
  updated_at: string;
  hasCredentials?: boolean;
}

interface AppTypeConfig {
  type: string;
  name: string;
  description: string;
  fields: {
    public: Record<string, FieldConfig>;
    sensitive: Record<string, FieldConfig>;
  };
}

interface FieldConfig {
  type: 'text' | 'password' | 'array' | 'select';
  label: string;
  required: boolean;
  description: string;
  default?: any;
  options?: string[];
}

interface TestResult {
  success: boolean;
  message: string;
}

const Applications: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [appTypes, setAppTypes] = useState<AppTypeConfig[]>([]);
  const [appTypeConfigs, setAppTypeConfigs] = useState<Record<string, AppTypeConfig>>({});
  const [loading, setLoading] = useState(true);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    status: 'active' as 'active' | 'inactive',
    config_data: {} as any
  });

  useEffect(() => {
    loadApplications();
    loadAppTypes();
  }, []);

  // Carregar configurações dos tipos de aplicações quando as aplicações são carregadas
  useEffect(() => {
    const loadConfigs = async () => {
      setLoadingConfigs(true);
      try {
        for (const app of applications) {
          if (!appTypeConfigs[app.type]) {
            await loadAppTypeConfig(app.type);
          }
        }
      } finally {
        setLoadingConfigs(false);
      }
    };
    
    if (applications.length > 0) {
      loadConfigs();
    }
  }, [applications, appTypeConfigs]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('authToken');
      console.log('🔍 Token encontrado:', token ? 'SIM' : 'NÃO');
      
      if (!token) {
        setError('Token não encontrado. Faça login novamente.');
        return;
      }
      
      const response = await fetch('/api/applications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 Status da resposta:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          setError('Token inválido. Faça login novamente.');
        } else {
          throw new Error('Erro ao carregar aplicações');
        }
        return;
      }

      const data = await response.json();
      setApplications(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  const loadAppTypes = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('🔍 Carregando tipos de aplicações...');
      console.log('🔍 Token encontrado:', token ? 'SIM' : 'NÃO');
      
      if (!token) {
        console.log('❌ Token não encontrado para carregar tipos');
        return;
      }

      const response = await fetch('/api/applications/types', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 Status da resposta types:', response.status);

      if (response.ok) {
        const types = await response.json();
        console.log('🔍 Tipos carregados:', types);
        setAppTypes(types);
      } else {
        console.log('❌ Erro ao carregar tipos:', response.status);
        const errorText = await response.text();
        console.log('❌ Erro detalhado:', errorText);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tipos de aplicações:', error);
    }
  };

  const getAppTypeConfig = (type: string): AppTypeConfig | null => {
    return appTypeConfigs[type] || null;
  };

  const loadAppTypeConfig = async (type: string) => {
    // Se já temos a configuração em cache, retornar
    if (appTypeConfigs[type]) {
      return appTypeConfigs[type];
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;

      const response = await fetch(`/api/applications/config/${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const config = await response.json();
        setAppTypeConfigs(prev => ({ ...prev, [type]: config }));
        return config;
      }
    } catch (error) {
      console.error(`❌ Erro ao buscar config para tipo '${type}':`, error);
    }

    return null;
  };

  const handleEdit = (app: Application) => {
    setSelectedApp(app);
    
    // Inicializar formulário com dados existentes
    const initialConfig = { ...app.config_data };
    
    console.log('🔍 Editando aplicação:', app);
    console.log('🔍 Configuração inicial:', initialConfig);
    
    // Aguardar um pouco para garantir que a configuração do tipo seja carregada
    setTimeout(() => {
      const appTypeConfig = getAppTypeConfig(app.type);
      if (appTypeConfig) {
        // Aplicar valores padrão para campos que não existem
        const enhancedConfig = { ...initialConfig };
        
        // Aplicar valores padrão dos campos públicos
        Object.entries(appTypeConfig.fields.public || {}).forEach(([fieldName, fieldConfig]) => {
          if (!enhancedConfig[fieldName] && fieldConfig.default !== undefined) {
            enhancedConfig[fieldName] = fieldConfig.default;
          }
        });
        
        // Aplicar valores padrão dos campos sensíveis
        Object.entries(appTypeConfig.fields.sensitive || {}).forEach(([fieldName, fieldConfig]) => {
          if (!enhancedConfig[fieldName] && fieldConfig.default !== undefined) {
            enhancedConfig[fieldName] = fieldConfig.default;
          }
        });
        
        console.log('🔍 Configuração aprimorada:', enhancedConfig);
        
        setFormData({
          name: app.name,
          status: app.status,
          config_data: enhancedConfig
        });
      } else {
        setFormData({
          name: app.name,
          status: app.status,
          config_data: initialConfig
        });
      }
    }, 100);
    
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedApp) return;

    try {
      setSaving(true);
      
      console.log('🔍 Salvando aplicação:', selectedApp.id);
      console.log('🔍 Dados a enviar:', formData);
      console.log('🔍 Dados serializados:', JSON.stringify(formData));
      
      const response = await fetch(`/api/applications/${selectedApp.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(formData)
      });

      console.log('🔍 Status da resposta:', response.status);
      console.log('🔍 Headers da resposta:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('🔍 Erro da resposta:', errorText);
        throw new Error(`Erro ao salvar aplicação: ${errorText}`);
      }

      const updatedApp = await response.json();
      console.log('🔍 Aplicação atualizada:', updatedApp);
      
      setApplications(prev => 
        prev.map(app => app.id === updatedApp.id ? updatedApp : app)
      );
      setEditDialogOpen(false);
      setTestResult({ success: true, message: 'Aplicação atualizada com sucesso!' });
    } catch (err) {
      console.error('❌ Erro ao salvar:', err);
      setTestResult({ 
        success: false, 
        message: err instanceof Error ? err.message : 'Erro ao salvar' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (app: Application) => {
    try {
      setTesting(true);
      setTestResult(null);
      
      console.log('🔍 Testando aplicação:', app.id);
      
      const response = await fetch(`/api/applications/${app.id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      console.log('🔍 Status da resposta:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('🔍 Erro da resposta:', errorText);
        throw new Error('Erro ao testar aplicação');
      }

      const result = await response.json();
      setTestResult(result);
    } catch (err) {
      setTestResult({ 
        success: false, 
        message: err instanceof Error ? err.message : 'Erro ao testar' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config_data: {
        ...prev.config_data,
        [field]: value
      }
    }));
  };

  const renderField = (fieldName: string, fieldConfig: FieldConfig) => {
    const value = formData.config_data[fieldName] || fieldConfig.default || '';
    
    console.log(`🔍 Renderizando campo ${fieldName}:`, {
      value,
      defaultValue: fieldConfig.default,
      configDataValue: formData.config_data[fieldName]
    });

    switch (fieldConfig.type) {
      case 'password':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="password"
            label={fieldConfig.label}
            value={value}
            onChange={(e) => handleConfigChange(fieldName, e.target.value)}
            helperText={fieldConfig.description}
            required={fieldConfig.required}
            margin="normal"
          />
        );
      
      case 'array':
        return (
          <TextField
            key={fieldName}
            fullWidth
            label={fieldConfig.label}
            value={Array.isArray(value) ? value.join(', ') : value}
            onChange={(e) => handleConfigChange(fieldName, e.target.value.split(',').map(s => s.trim()))}
            helperText={fieldConfig.description}
            required={fieldConfig.required}
            margin="normal"
          />
        );
      
      case 'select':
        return (
          <FormControl key={fieldName} fullWidth margin="normal" required={fieldConfig.required}>
            <InputLabel>{fieldConfig.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleConfigChange(fieldName, e.target.value)}
              label={fieldConfig.label}
            >
              {fieldConfig.options?.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      default:
        return (
          <TextField
            key={fieldName}
            fullWidth
            label={fieldConfig.label}
            value={value}
            onChange={(e) => handleConfigChange(fieldName, e.target.value)}
            helperText={fieldConfig.description}
            required={fieldConfig.required}
            margin="normal"
          />
        );
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'default';
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'gmail':
        return '📧';
      case 'zoho_desk':
        return '🎫';
      case 'slack':
        return '💬';
      case 'teams':
        return '💼';
      default:
        return '🔧';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Carregando aplicações...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Aplicações
        </Typography>
      </Box>

      {testResult && (
        <Alert 
          severity={testResult.success ? 'success' : 'error'} 
          sx={{ mb: 2 }}
          onClose={() => setTestResult(null)}
        >
          {testResult.message}
        </Alert>
      )}

      {loadingConfigs && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Carregando configurações das aplicações...
        </Alert>
      )}

      <Grid container spacing={3}>
        {applications.map((app) => {
          const appTypeConfig = getAppTypeConfig(app.type);
          
          return (
            <Grid item xs={12} md={6} lg={4} key={app.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                       <Box>
                     <Typography variant="h6" component="h2">
                       {getTypeIcon(app.type)} {app.name}
                     </Typography>
                     <Typography variant="body2" color="text.secondary">
                       {appTypeConfig?.description || `Aplicação ${app.type}`}
                     </Typography>
                   </Box>
                    <Chip 
                      label={app.status} 
                      color={getStatusColor(app.status)}
                      size="small"
                    />
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Tipo: {app.type}
                    </Typography>
                    {app.hasCredentials && (
                      <Chip 
                        icon={<LockIcon />}
                        label="Credenciais configuradas" 
                        color="success" 
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(app)}
                      variant="outlined"
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      startIcon={<TestIcon />}
                      onClick={() => handleTest(app)}
                      variant="outlined"
                      disabled={testing}
                    >
                      Testar
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Dialog de Edição */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Editar Aplicação: {selectedApp?.name}
        </DialogTitle>
        <DialogContent>
          {selectedApp && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
                  label="Status"
                >
                  <MenuItem value="active">Ativo</MenuItem>
                  <MenuItem value="inactive">Inativo</MenuItem>
                </Select>
              </FormControl>

              {selectedApp.type && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Configuração da Aplicação
                  </Typography>
                  
                                     {(() => {
                     const appTypeConfig = getAppTypeConfig(selectedApp.type);
                     
                     if (!appTypeConfig) {
                       return (
                         <Box sx={{ mt: 2 }}>
                           <Alert severity="warning" sx={{ mb: 2 }}>
                             Configuração para o tipo "{selectedApp.type}" não encontrada.
                           </Alert>
                           <Button 
                             variant="outlined" 
                             onClick={() => loadAppTypeConfig(selectedApp.type)}
                             disabled={loadingConfigs}
                           >
                             {loadingConfigs ? 'Carregando...' : 'Carregar Configuração'}
                           </Button>
                         </Box>
                       );
                     }

                     return (
                       <>
                         {/* Campos Públicos */}
                         {Object.keys(appTypeConfig.fields?.public || {}).length > 0 && (
                           <Accordion defaultExpanded>
                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                               <ListItemIcon>
                                 <PublicIcon />
                               </ListItemIcon>
                               <Typography>Campos Públicos</Typography>
                             </AccordionSummary>
                             <AccordionDetails>
                               <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                 Estes campos são armazenados no banco de dados
                               </Typography>
                               {Object.entries(appTypeConfig.fields.public || {}).map(([fieldName, fieldConfig]) => 
                                 renderField(fieldName, fieldConfig)
                               )}
                             </AccordionDetails>
                           </Accordion>
                         )}

                         {/* Campos Sensíveis */}
                         {Object.keys(appTypeConfig.fields?.sensitive || {}).length > 0 && (
                           <Accordion defaultExpanded>
                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                               <ListItemIcon>
                                 <SecurityIcon />
                               </ListItemIcon>
                               <Typography>Campos Sensíveis</Typography>
                             </AccordionSummary>
                             <AccordionDetails>
                               <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                 Estes campos são criptografados e armazenados em arquivo seguro
                               </Typography>
                               {Object.entries(appTypeConfig.fields.sensitive || {}).map(([fieldName, fieldConfig]) => 
                                 renderField(fieldName, fieldConfig)
                               )}
                             </AccordionDetails>
                           </Accordion>
                         )}

                         {/* Se não há configuração de campos, mostrar mensagem */}
                         {(!appTypeConfig.fields?.public || Object.keys(appTypeConfig.fields.public).length === 0) &&
                          (!appTypeConfig.fields?.sensitive || Object.keys(appTypeConfig.fields.sensitive).length === 0) && (
                           <Alert severity="info" sx={{ mt: 2 }}>
                             Nenhum campo de configuração definido para este tipo de aplicação.
                           </Alert>
                         )}
                       </>
                     );
                   })()}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Applications;
